/**
 * API utility for making requests to the backend
 */

/**
 * Returns true when the given hostname is a local / private-network address.
 * This covers localhost, loopback, and all RFC-1918 private IP ranges
 * (192.168.x.x, 10.x.x.x, 172.16-31.x.x) so that LAN access during
 * development resolves images against the local backend, not production.
 */
const isLocalHostname = (hostname: string): boolean => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  // RFC-1918 private ranges
  if (/^192\.168\./.test(hostname)) return true;
  if (/^10\./.test(hostname)) return true;
  if (/^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname)) return true;
  return false;
};

/**
 * Derive the backend base URL (no trailing /api).
 * - If REACT_APP_API_URL is set, strip /api and use that.
 * - If running on a local/LAN hostname, point to port 5000 on the SAME host
 *   so that browsers on other LAN devices get the correct backend address.
 * - Otherwise fall back to the production API.
 */
const getBackendBase = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/api$/, '');
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (isLocalHostname(hostname)) {
      // Use the same hostname as the page so LAN devices always reach the right server
      return `http://${hostname}:5000`;
    }
    return 'https://api.skillseek.co';
  }
  return 'http://localhost:5000';
};

// Get the base API URL depending on the environment
const getBaseUrl = (): string => {
  // Check if environment variable is set (highest priority)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return `${getBackendBase()}/api`;
};

// Base URL for API requests
export const API_BASE_URL = getBaseUrl();

// Base backend URL (without /api) for uploads and images
export const BACKEND_BASE_URL = getBackendBase();

// Global utility to normalize all image/file paths
export const normalizeImageUrl = (imagePath: string | null | undefined, fallback: string = '/assets/images/service-one.png'): string => {
  if (!imagePath) return fallback;
  
  let cleanPath = imagePath;
  const uploadsIndex = cleanPath.indexOf('/uploads/');
  if (uploadsIndex !== -1 && (cleanPath.startsWith('http://') || cleanPath.startsWith('https://'))) {
    cleanPath = cleanPath.substring(uploadsIndex + 1);
  }
  
  if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) return cleanPath;
  if (cleanPath.startsWith('/uploads/') || cleanPath.startsWith('uploads/')) {
    return `${BACKEND_BASE_URL}/${cleanPath.replace(/^\//, '')}`;
  }
  return `${BACKEND_BASE_URL}/uploads/${cleanPath}`;
};

// Log the API base URL for debugging
console.log('🔗 API Base URL:', API_BASE_URL);
console.log('🔗 Backend Base URL:', BACKEND_BASE_URL);

/**
 * Makes a fetch request to the API with the correct base URL
 * @param endpoint - API endpoint (should start with '/')
 * @param options - Fetch options
* @returns Promise with the fetch response
 */
export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  // Ensure endpoint starts with '/'
  let normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  // If the base URL ends with /api and endpoint starts with /api, remove one /api
  if (API_BASE_URL.endsWith('/api') && normalizedEndpoint.startsWith('/api')) {
    normalizedEndpoint = normalizedEndpoint.substring(4); // Remove '/api' from endpoint
  }

  // Combine base URL with endpoint
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  console.log(`Making API request to: ${url}`);

  // Set default headers if not provided
  if (!options.headers) {
    options.headers = {
      'Content-Type': 'application/json',
    };
  }

  // Add authentication token if available
  // Check for customer token first for customer endpoints, then admin token
  let token = localStorage.getItem('skillseek_customer_token');
  if (!token) {
    token = localStorage.getItem('skillseek_talent_token');
  }
  if (!token) {
    token = localStorage.getItem('skillseek_admin_token');
  }

  if (token) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
  }

  try {
    const response = await fetch(url, options);

    // Check if response is ok (status in the range 200-299)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API request error for ${endpoint}:`, error);
    throw error;
  }
};

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (endpoint: string, options: RequestInit = {}) =>
    apiRequest(endpoint, { ...options, method: 'GET' }),

  post: (endpoint: string, data: any, options: RequestInit = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    }),

  postFormData: (endpoint: string, formData: FormData, options: RequestInit = {}) => {
    // Ensure endpoint starts with '/'
    let normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // If the base URL ends with /api and endpoint starts with /api, remove one /api
    if (API_BASE_URL.endsWith('/api') && normalizedEndpoint.startsWith('/api')) {
      normalizedEndpoint = normalizedEndpoint.substring(4); // Remove '/api' from endpoint
    }

    const url = `${API_BASE_URL}${normalizedEndpoint}`;

    console.log(`Making FormData API request to: ${url}`);

    // Set default headers if not provided
    if (!options.headers) {
      options.headers = {};
    }

    // Add authentication token if available
    // Check for customer token first for customer endpoints, then admin token
    let token = localStorage.getItem('skillseek_customer_token');
    if (!token) {
      token = localStorage.getItem('skillseek_talent_token');
    }
    if (!token) {
      token = localStorage.getItem('skillseek_admin_token');
    }

    if (token) {
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      };
    }

    return fetch(url, {
      ...options,
      method: 'POST',
      body: formData
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API request failed with status ${response.status}`);
      }
      return response.json();
    });
  },

  put: (endpoint: string, data: any, options: RequestInit = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  patch: (endpoint: string, data: any, options: RequestInit = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  delete: (endpoint: string, options: RequestInit = {}) =>
    apiRequest(endpoint, { ...options, method: 'DELETE' }),
};

export default api; 